<template>
    <div class="history-modal-parameter-component">
        <div class="parameter-card-header">
            <div class="profile-info">
                <div class="picture">
                    <img
                        v-if="item.userPhotoUrl || item.photo"
                        :src="item.userPhotoUrl || item.photo"
                        alt="User photo"
                    />
                    <AvatarPlaceholder
                        v-else
                        size="46px"
                        :text="picturePlaceholder"
                    />
                </div>
                <div class="user-meta">
                    <div class="name">
                        {{ `${item.firstName || ""} ${item.lastName || ""}`.trim() || "-" }}
                    </div>
                    <div class="sub-meta">
                        <span class="appointment-id">ID: <strong>{{ item.id }}</strong></span>
                        <template v-if="item.turnoName">
                            <span class="meta-separator">•</span>
                            <span class="shift">{{ item.turnoName }}</span>
                        </template>
                        <template v-if="item.resource || item.resourceName">
                            <span class="meta-separator">•</span>
                            <span class="resource-name">{{ item.resource || item.resourceName }}</span>
                        </template>
                    </div>
                </div>
            </div>

            <div class="header-right">
                <div class="date-badge">
                    <span class="date-text">{{ formatDate(item.eventTime) }}</span>
                </div>
                <div
                    v-if="item.statusName"
                    class="status-pill"
                    :style="statusStyle"
                >
                    {{ item.statusName }}
                </div>
            </div>
        </div>

        <div class="parameter-card-body">
            <div class="params-header-title">
                <span class="title-text">{{ $t("Operator.CustomParameters") }}</span>
                <span class="params-count">({{ item.parameters ? item.parameters.length : 0 }})</span>
            </div>

            <div class="params-grid">
                <div
                    v-for="(param, pIdx) in item.parameters"
                    :key="pIdx"
                    class="param-item"
                >
                    <div class="param-label-row">
                        <span class="param-label">{{ param.parameterLabel || param.parameterName }}</span>
                        <span
                            v-if="param.parameterLabel && param.parameterName && param.parameterLabel !== param.parameterName"
                            class="param-tech-name"
                        >
                            ({{ param.parameterName }})
                        </span>
                    </div>
                    <div class="param-value-container">
                        <span class="param-value">{{ param.displayValue || param.rawValue }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { avatarText } from "@core/utils/filter";
import AvatarPlaceholder from "@core/components/avatar-placeholder/index.vue";
import moment from "moment";

export default {
    name: "ItemParameter",
    components: {
        AvatarPlaceholder
    },
    props: {
        item: {
            type: Object,
            required: true,
            default: () => ({})
        }
    },
    computed: {
        picturePlaceholder() {
            return avatarText(this.item.firstName, this.item.lastName);
        },
        statusStyle() {
            const color = this.item.statusColor || "#974900";
            return {
                backgroundColor: `${color}18`,
                color: color,
                borderColor: `${color}40`
            };
        }
    },
    methods: {
        formatDate(date) {
            if (!date) return "-";
            const language = this.$cookies.get("userInfo")?.language;
            if (language === "pt-BR") {
                return moment(date).format("DD/MM/YYYY HH:mm");
            }
            return moment(date).format("MM/DD/YYYY HH:mm");
        }
    }
};
</script>

<style lang="scss" scoped>
.history-modal-parameter-component {
    border-radius: 16px;
    border: 1px solid #cfc4be;
    background: #ffffff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    transition: box-shadow 0.2s ease, border-color 0.2s ease;

    &:hover {
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
        border-color: #b5a9a1;
    }

    .parameter-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px 24px;
        background-color: #faf9f8;
        border-bottom: 1px solid #ede8e3;
        flex-wrap: wrap;
        gap: 16px;

        .profile-info {
            display: flex;
            align-items: center;
            gap: 14px;

            .picture {
                display: flex;
                align-items: center;
                justify-content: center;

                img {
                    width: 46px;
                    height: 46px;
                    object-fit: cover;
                    border-radius: 50%;
                    border: 2px solid #ffffff;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
                }
            }

            .user-meta {
                display: flex;
                flex-direction: column;
                gap: 3px;

                .name {
                    color: #2e2825;
                    font-size: 18px;
                    font-weight: 700;
                    line-height: 24px;
                }

                .sub-meta {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 15px;
                    color: #6e645e;
                    font-weight: 500;
                    flex-wrap: wrap;

                    .appointment-id {
                        strong {
                            color: #2e2825;
                            font-weight: 700;
                        }
                    }

                    .meta-separator {
                        color: #b5aba4;
                        font-weight: 700;
                    }

                    .shift,
                    .resource-name {
                        color: #594f4a;
                        font-weight: 600;
                    }
                }
            }
        }

        .header-right {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;

            .date-badge {
                font-size: 15px;
                font-weight: 600;
                color: #2e2825;
                background-color: #ffffff;
                padding: 8px 14px;
                border-radius: 8px;
                border: 1px solid #dcd3cb;
            }

            .status-pill {
                display: inline-flex;
                align-items: center;
                padding: 8px 16px;
                border-radius: 20px;
                font-size: 14px;
                font-weight: 700;
                border: 1px solid transparent;
                white-space: nowrap;
                letter-spacing: 0.2px;
            }
        }
    }

    .parameter-card-body {
        padding: 20px 24px 24px;

        .params-header-title {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 14px;

            .title-text {
                font-size: 14px;
                font-weight: 800;
                color: #7d726b;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            .params-count {
                font-size: 14px;
                font-weight: 700;
                color: #974900;
            }
        }

        .params-grid {
            display: flex;
            flex-direction: column;
            gap: 10px;

            .param-item {
                display: flex;
                flex-direction: column;
                background-color: #fafafa;
                border: 1px solid #e5dfd8;
                border-radius: 10px;
                padding: 14px 18px;
                transition: border-color 0.15s ease;

                &:hover {
                    border-color: #ffd8b8;
                }

                .param-label-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 6px;

                    .param-label {
                        font-size: 16px;
                        font-weight: 700;
                        color: #974900;
                    }

                    .param-tech-name {
                        font-size: 13px;
                        color: #8c827c;
                    }
                }

                .param-value-container {
                    .param-value {
                        font-size: 17px;
                        line-height: 24px;
                        color: #2e2825;
                        font-weight: 600;
                        white-space: pre-wrap;
                        word-break: break-word;
                    }
                }
            }
        }
    }
}

@media (max-width: 600px) {
    .history-modal-parameter-component {
        .parameter-card-header {
            flex-direction: column;
            align-items: flex-start;

            .header-right {
                width: 100%;
                justify-content: space-between;
            }
        }

        .parameter-card-body {
            padding: 14px 16px 18px;
        }
    }
}
</style>
